import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ChangeDetectorRef,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  HostListener
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_LANGUAGE } from '../../constants';
import {
  DefaultTableConfig,
  DialogState,
  SnackbarMessageService,
  TableColumns,
  TableConfig,
} from '@ngx-interact-ui/core';
import { Subject, firstValueFrom } from 'rxjs';
import { tap, takeUntil, filter } from 'rxjs/operators';
import { ICertificate } from '../../interfaces';
import { CertificateService } from '../../services/certificate.service';
import { AmplitudeService } from '../../services/amplitude.service';
import { ApplicationEventService } from '../../services/application-events.service';
import moment from 'moment';

@Component({
  selector: 'ph-certificates',
  templateUrl: './certificates.component.html',
  styleUrls: ['./certificates.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CertificatesComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() public projectId!: number;
  @Input() public projectCode!: string;
  @Input() public projectName: string;
  @Input() public accessToken!: string;
  @Input() public language!: string;
  @ViewChild('insPara') public insPara: ElementRef<HTMLParagraphElement>;
  @ViewChild('fileInput') public fileInput: ElementRef<HTMLInputElement>;
  @ViewChild('cerActionTpl', { static: true })
  public cerActionTpl: TemplateRef<any>;
  @ViewChild('contextMenuTemplate') public contextMenuTemplate:
    | TemplateRef<any>
    | undefined;

  get certificate() {
    return this.certificateService.state;
  }

  public absoluteUrl: string;

  public certificateCreatorDialog = new DialogState<void>();
  public certificateDeleteDialog = new DialogState<void, {}>();
  public isShowInstruction = false;
  public isCreating = false;
  public cerGridColumns: any[] = [];
  public cerFileName = '';
  public fileInvalidError = '';
  public tableParameters: {
    totalRecords: number;
    incompleteResults: boolean;
    limit: number;
    offset: number;
  };
  public tableColumns: TableColumns[] = [];
  public basicTableColumns: TableColumns[] = [];
  public tableData: ICertificate[] = [];
  public totalCertificates = 0;
  public deleteCertConfirmText: String;
  public certificateToDelete: string;
  public tableWithThreeDotMenuConfiguration: TableConfig = {
    ...DefaultTableConfig,
    horizontalScroll: this.isTabletView(),
    verticalScroll: false,
  };

  private unsubscribe$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private snackbarMessageService: SnackbarMessageService,
    private readonly certificateService: CertificateService,
    private readonly amplitudeService: AmplitudeService,
    private readonly applicationEventService: ApplicationEventService
  ) {}

  public ngOnInit() {
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.translate.use(DEFAULT_LANGUAGE);

    const currentUrl = window.location.href;
    this.absoluteUrl = currentUrl;
  }

  public ngAfterViewInit() {
    this.initUI();
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.projectId && this.accessToken) {
      this.initData();
    }
    if (changes.language) {
      this.translate.use(changes.language.currentValue || DEFAULT_LANGUAGE);
    }
  }

   private isTabletView(): boolean {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return width >= 360 && width <= 1023;
    }
    return false;
  }

  /**
   * Handle window resize to update table horizontal scroll settings
   */
  @HostListener('window:resize', ['$event'])
  private handleResize(event?: Event): void {
    const shouldEnableHorizontalScroll = this.isTabletView();

    // Only update if the configuration actually changes
    if (this.tableWithThreeDotMenuConfiguration.horizontalScroll !== shouldEnableHorizontalScroll) {
      this.tableWithThreeDotMenuConfiguration = {
        ...this.tableWithThreeDotMenuConfiguration,
        horizontalScroll: shouldEnableHorizontalScroll,
        fixedColumnWidth: false // Ensure flexible column widths
      };

      // Re-initialize table columns to ensure pinning works with new config
      this.initUI();

      // Trigger change detection to update the view
      this.cdr.detectChanges();
    }
  }

  public openCreateCerDialog() {
    const event =
      this.applicationEventService.clickOnTheCreateCertificateButtonInTheProjectBuildingIntegrationsPage(
        this.projectId
      );
    this.amplitudeService.logEvent(event.name, event.properties);
    this.isCreating = false;
    this.certificateCreatorDialog.open();

    setTimeout(() => {
      this.removeCerFile();
    }, 0);
  }

  public onChangeFile() {
    this.fileInvalidError = '';
    const file = this.fileInput.nativeElement.files[0];
    if (!file) {
      return;
    }
    const filename = file.name;
    const ext = filename.substring(filename.lastIndexOf('.') + 1);
    if (ext.toLowerCase() !== 'csr') {
      this.fileInvalidError = this.translate.instant(
        'building_integrations.INVALID_CERTIFICATE_FILE'
      );
      return;
    }
    this.cerFileName = filename;
  }

  public removeCerFile() {
    this.cerFileName = '';
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.cdr?.markForCheck();
  }

  public createCertificate() {
    const event =
      this.applicationEventService.clickOnTheCreateButtonInTheCertificatesDialog(
        this.projectId
      );
    this.amplitudeService.logEvent(event.name, event.properties);
    this.isCreating = true;

    const formData: FormData = new FormData();
    const file = this.fileInput.nativeElement.files[0];

    formData.append('projectId', this.projectId.toString());
    formData.append('csrFile', file, file.name);

    this.certificateService
      .createCertificate(formData, this.accessToken)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => {
          this.certificateCreatorDialog.cancel();
          this.translate
            .get('building_integrations.CERTIFICATE_CREATED')
            .subscribe((text) => {
              this.snackbarMessageService.success({
                message: text,
              });
            });
          this.initData();
        },
        () => {
          this.certificateCreatorDialog.cancel();
          this.translate
            .get('building_integrations.CERTIFICATE_CREATE_FAILED')
            .subscribe((text) => {
              this.snackbarMessageService.warning({
                message: text,
              });
            });
          this.isCreating = false;
        }
      );
  }

  public deleteCertificate(certId: string) {
    const event =
      this.applicationEventService.clickOnTheDeleteButtonInTheDeleteCertificateDialog(
        this.projectId
      );
    this.amplitudeService.logEvent(event.name, event.properties);
    this.certificateDeleteDialog.cancel();
    this.certificateService
      .deleteCertificate(certId, this.accessToken)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.translate
          .get('building_integrations.CERTIFICATE_IS_DELETED')
          .subscribe((text) => {
            this.snackbarMessageService.success({
              message: text,
            });
          });
        this.initData();
      });
  }

  public downloadCer(certificate: ICertificate) {
    const event =
      this.applicationEventService.clickOnTheDownloadOptionInCertificatesTable(
        this.projectId
      );
    this.amplitudeService.logEvent(event.name, event.properties);
    const blob = new Blob([new Uint8Array(certificate.certificateZip)]);
    const downloadLink = document.createElement('a');
    const objectUrl = window.URL.createObjectURL(blob);

    downloadLink.href = objectUrl;
    downloadLink.download = certificate.fileName;
    downloadLink.click();
    window.URL.revokeObjectURL(objectUrl);
  }

  public pageNavigation(newPage: number) {
    this.tableParameters.offset = newPage;
  }

  public async assignCertificateToDelete(certificate: ICertificate) {
    const event =
      this.applicationEventService.clickOnTheDeleteOptionInTheCertificatesTable(
        this.projectId
      );
    this.amplitudeService.logEvent(event.name, event.properties);
    this.certificateToDelete = certificate.certId;
    this.deleteCertConfirmText = await firstValueFrom(
      this.translate.get('building_integrations.DELETE_CERTIFICATE_$1_ALERT', {
        name: certificate.fileName,
      })
    );
    this.certificateDeleteDialog.open();
  }

  public onInstructionKey(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.isShowInstruction = !this.isShowInstruction;
    }
  }
  /**
   * Init UI elements
   */
  private initUI() {
    this.tableWithThreeDotMenuConfiguration.fixedColumnWidth = false;
    this.tableColumns = [
      {
        key: 'fileName',
        title: 'File name',
        orderEnabled: false,
        cssClass: { name: 'col-name', includeHeader: true },
        width: '60%',
      },
      {
        key: 'creationDate',
        title: 'Creation date',
        orderEnabled: true,
        cssClass: { name: 'col-createtime', includeHeader: true },
        width: '15%',
      },
      {
        key: 'expiryDate',
        title: 'Expiry date',
        orderEnabled: true,
        cssClass: { name: 'col-type', includeHeader: true },
        width: '15%',
      },
      {
        key: '',
        title: '',
        orderEnabled: false,
        cellTemplate: this.contextMenuTemplate,
        cssClass: { name: 'col-context', includeHeader: false },
        width: '10%',
      },
    ];
  }

  public logClickOnCancelEvent(): void {
    const event = this.applicationEventService.clickOnTheCancelButtonInTheCertificatesDialog(this.projectId);
    this.amplitudeService.logEvent(event.name, event.properties);
  }

  public logClickOnCancelEventInCreateCertificate(): void {
    const event = this.applicationEventService.clickOnTheCancelButtonInTheDeleteCertificateDialog(this.projectId);
    this.amplitudeService.logEvent(event.name, event.properties);
  }

  /**
   * Init data
   */
  private initData() {
    // now projectId is passed as an input
    this.certificateService
      .fetchCertificates(this.projectId, this.accessToken)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.certificate.certificates$
      .pipe(
        tap((certificates: any) => {
          if (certificates && certificates.data && certificates.data.length > 0) {
            this.totalCertificates = certificates?.data.length;
            this.tableData = certificates.data.map((item: any) => ({
              ...item,
              fileName: item.fileName || item.certificateFileName,
              creationDate: moment(item.creationDate).format('M/D/YYYY'),
              expiryDate: moment(item.expiryDate).format('M/D/YYYY'),
            }));
            this.tableParameters = {
              totalRecords: certificates.length,
              incompleteResults: false,
              limit: 10,
              offset: 1,
            };
            this.cdr.markForCheck();
          } else {
            this.totalCertificates = 0;
            this.cdr.markForCheck();
          }
        }),
        filter((list) => !list.length),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        if (this.totalCertificates) {
          setTimeout(() => {
            const link = this.insPara?.nativeElement.querySelector('a');
            if (link) {
              link.addEventListener('click', () => {
                this.isShowInstruction = true;
                this.cdr.markForCheck();
              });
            }
          }, 0);
          this.isShowInstruction = false;
        } else {
          this.totalCertificates = 0;
          this.tableData = [];
          this.cdr.markForCheck();
        }
      });
  }
}
